// Section 12 "the health panel shows the current illness multiplier with its breakdown so the
// player can see which lever they are missing": the multiplier with its factor list, and the
// levers (well, clinic, sick water carriers, crowding, contagion, quarantine) named in words as
// in place / missing / in effect, never by colour alone. Props only.
import type { HealthLever, HealthVM } from '../../viewModels/health';
import { times } from '../format';
import { healthText } from '../text/en';
import { ExplainNumber } from './explain-number';

interface Props {
  multiplier: HealthVM['multiplier'];
  levers: readonly HealthLever[];
}

/** Buildings are levers you add; the rest are conditions you remove. */
function status(lever: HealthLever): { text: string; bad: boolean } {
  switch (lever.id) {
    case 'well':
    case 'clinic':
      return lever.good
        ? { text: healthText.leverGood, bad: false }
        : { text: healthText.leverMissing, bad: true };
    case 'quarantine':
      return lever.good
        ? { text: healthText.leverActive, bad: false }
        : { text: healthText.leverClear, bad: false };
    case 'sick-carrier':
    case 'crowding':
    case 'contagion':
      return lever.good
        ? { text: healthText.leverClear, bad: false }
        : { text: `${healthText.leverActive} ${times(lever.factor)}`, bad: true };
  }
}

export function MultiplierPanel({ multiplier, levers }: Props) {
  return (
    <section class="health__section health__multiplier" aria-labelledby="health-multiplier">
      <h3 id="health-multiplier">{healthText.multiplier}</h3>
      {/* A div, not a p: the factor disclosure is a <details> (M6: no invalid nesting). */}
      <div class="health__multiplier-value">
        <ExplainNumber
          value={times(multiplier.value)}
          factors={multiplier.factors}
          label={healthText.multiplier}
        />{' '}
        <span class="hint">
          {multiplier.asRolled && multiplier.turn !== null
            ? healthText.multiplierAsRolled(multiplier.turn)
            : healthText.multiplierNow}
        </span>
      </div>
      <p class="hint">{healthText.multiplierHint}</p>
      <h4>{healthText.levers}</h4>
      <ul class="health__levers">
        {levers.map((lever) => {
          const s = status(lever);
          return (
            <li key={lever.id} class={s.bad ? 'is-bad' : 'is-good'}>
              <span class="health__lever-status">
                <span aria-hidden="true">{s.bad ? '▲' : '○'}</span> {s.text}
              </span>
              : {healthText.lever[lever.id]}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
